import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SpaLink from './SpaLink';
import ProductsMenu, { ProductIcon } from './ProductsMenu';
import { LIVE_PRODUCTS, HOME_META, applyMeta } from './products';
import { GROUP_GAMES, COMPETITIONS, GAME_COUNT, GAMES_PLAN } from './gamesData';   // Oct 5, 2026 — the Games band

// GA4 tracking helper - safe to call even if gtag isn't loaded (e.g. on authenticated routes)
const trackEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};

/**
 * LandingPage — the home page, teetimeorganizer.net (Nov 1, 2026 rewrite).
 * The generic Tee Time Organizer page: what TTO is, the products ("Choose what
 * you run") and what they share. Each product's page carries its own pricing:
 * /groups (Weekly Golf Groups, GroupsPage.js) and /events (Tournaments &
 * Outings, EventsPage.js). Products come from products.js.
 * Plan: claude/Products_Menu_And_Home_Page_Plan_Nov01.md
 */
const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showContactModal, setShowContactModal] = useState(false);
  // `website` is the honeypot — see the hidden input in the contact modal and
  // the rules in backend/src/routes/contactRoutes.js. It rides inside
  // contactForm so it spreads into the POST body with everything else.
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '', organizationName: '', approximateSize: '', website: '' });
  // Stamped when the modal OPENS, not at page load: the landing page can sit
  // in a tab for hours before anyone clicks Contact Us, and a drop is silent.
  const [renderedAt, setRenderedAt] = useState(() => Date.now());
  const [inquiryType, setInquiryType] = useState('general'); // 'general' | 'club'
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const [contactError, setContactError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Capture UTM parameters on landing for attribution through to signup.
  // Stored in sessionStorage (last-touch attribution, tab-scoped) so they
  // survive navigation to /register and make it into the registration POST
  // body. The RegisterPage reads these values back.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const utmData = {
      utm_source:   params.get('utm_source')   || null,
      utm_medium:   params.get('utm_medium')   || null,
      utm_campaign: params.get('utm_campaign') || null,
      utm_content:  params.get('utm_content')  || null,
      landing_page: window.location.pathname,
    };
    // Only overwrite sessionStorage if this visit actually has UTM data,
    // or if nothing's stored yet. This preserves attribution across
    // in-tab navigation.
    const hasAnyUtm = utmData.utm_source || utmData.utm_medium ||
                      utmData.utm_campaign || utmData.utm_content;
    const existing = sessionStorage.getItem('tto_attribution');
    if (hasAnyUtm || !existing) {
      sessionStorage.setItem('tto_attribution', JSON.stringify(utmData));
    }
  }, []);

  // Check for stored player token
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    
    if (params.get('landing') === 'true') return;
    if (params.get('from') === 'guide') return;
    
    const storedToken = localStorage.getItem('playerPortalToken');
    const tokenExpiry = localStorage.getItem('playerPortalTokenExpiry');
    
    if (storedToken && tokenExpiry) {
      const now = new Date().getTime();
      const expiry = parseInt(tokenExpiry, 10);
      
      if (now < expiry) {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone === true;
        
        if (isIOS || isPWA) {
          navigate(`/player?token=${storedToken}`);
          return;
        }
      } else {
        localStorage.removeItem('playerPortalToken');
        localStorage.removeItem('playerPortalTokenExpiry');
      }
    }
    
    if (params.get('contact') === 'true') {
      setShowContactModal(true);
    }
  }, [navigate, location]);

  // Marketing meta tags, Open Graph, canonical, and JSON-LD structured data
  // live statically in public/index.html (single source of truth). Re-applying
  // the home values here keeps the tab title and canonical right when a visitor
  // comes back client-side from /games, /groups or /events.
  useEffect(() => {
    applyMeta(HOME_META);
  }, []);

  // Handle hash scrolling
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]);

  // Re-stamp every time the contact modal opens (see renderedAt above).
  useEffect(() => {
    if (showContactModal) setRenderedAt(Date.now());
  }, [showContactModal]);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    setContactError('');
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactForm, inquiryType, formRenderedAt: renderedAt })
      });
      
      if (response.ok) {
        setContactSuccess(true);
        setContactForm({ name: '', email: '', message: '', organizationName: '', approximateSize: '', website: '' });
      } else {
        setContactError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setContactError('Failed to send message. Please try again.');
    } finally {
      setContactSubmitting(false);
    }
  };

  // Close-and-reset helper for the contact modal — keeps Club-state from leaking
  // into the next open (e.g. footer "Contact Us" after a Club submission).
  const closeContactModal = () => {
    setShowContactModal(false);
    setContactSuccess(false);
    setInquiryType('general');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Colors
  const colors = {
    green900: '#14532d', green800: '#166534', green700: '#15803d', green600: '#16a34a',
    green500: '#22c55e', green100: '#dcfce7', green50: '#f0fdf4',
    gray900: '#111827', gray800: '#1f2937', gray700: '#374151', gray600: '#4b5563',
    gray500: '#6b7280', gray400: '#9ca3af', gray300: '#d1d5db', gray200: '#e5e7eb',
    gray100: '#f3f4f6', gray50: '#f9fafb',
    amber500: '#f59e0b', amber400: '#fbbf24',
  };

  // Product cards (Nov 1, 2026). Up to three live products show as large cards
  // with a few highlights; four or five switch to compact tiles (name, line,
  // price, button) so the row still reads at a glance. See products.js.
  const compactCards = LIVE_PRODUCTS.length > 3;
  const cardColumns = isMobile
    ? '1fr'
    : compactCards
      ? 'repeat(auto-fit, minmax(210px, 1fr))'
      : `repeat(${LIVE_PRODUCTS.length}, minmax(0, 1fr))`;

  // What every product shares. Tags say which products a feature belongs to.
  const sharedFeatures = [
    { title: 'Tee times & sign-ups', desc: 'Publish a tee time and players reply with one tap. Waitlists fill themselves.', groups: true, events: false },
    { title: 'Registration & teams', desc: 'Players or team captains sign up online, or import your list in one step.', groups: false, events: true },
    { title: 'Pairings & flights', desc: 'Balanced foursomes, shotgun starts, cart signs and printed scorecards.', groups: true, events: true },
    { title: 'Games', desc: 'Skins, Nassau, Stableford, best ball, scramble and more, with the money worked out for you.', groups: true, events: true },
    { title: 'Live scoring', desc: 'Score on any phone. The leaderboard updates on phones and on the clubhouse TV.', groups: true, events: true },
    { title: 'Player stats & recaps', desc: 'Every round adds to each player’s stats, with a recap of the day’s highlights.', groups: true, events: true },
    { title: 'Handicaps & GHIN', desc: 'Course handicaps worked out for each player’s tees, and scores posted to GHIN.', groups: true, events: true },
    { title: 'Reminders & messages', desc: 'Email and text reminders with the weather forecast, sent automatically.', groups: true, events: true },
  ];

  const navLink = { padding: '10px 16px', fontSize: '14px', fontWeight: 600, color: colors.gray700, cursor: 'pointer', borderRadius: '8px', textDecoration: 'none', whiteSpace: 'nowrap' };

  return (
    <div style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }}>

      {/* Navigation — Products menu (Nov 1, 2026) */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(255, 255, 255, 0.97)', backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${colors.gray200}`, padding: isMobile ? '10px 16px' : '12px 32px',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '14px' }}>
            <div style={{
              width: isMobile ? '40px' : '52px', height: isMobile ? '40px' : '52px', background: '#fff', borderRadius: '12px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)', border: `1px solid ${colors.gray200}`, overflow: 'hidden',
            }}>
              <img src="/logo.png" alt="Tee Time Organizer Logo" width="44" height="44" style={{ width: isMobile ? '32px' : '44px', height: isMobile ? '32px' : '44px', objectFit: 'contain' }} />
            </div>
            <div>
              <div style={{ fontSize: isMobile ? '16px' : '20px', fontWeight: 800, color: colors.gray900, letterSpacing: '-0.5px' }}>Tee Time Organizer</div>
              {!isMobile && <div style={{ fontSize: '12px', color: colors.gray500, fontWeight: 500 }}>Golf, organized</div>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {!isMobile && (
              <>
                <ProductsMenu isMobile={false} location="home_header" />
                <SpaLink to="/games" onClick={() => trackEvent('games_link_click', { location: 'header' })} style={navLink}>Games</SpaLink>
                <SpaLink to="/guides" style={navLink}>Guides</SpaLink>
              </>
            )}
            <button onClick={() => { trackEvent('demo_cta_click', { location: 'header' }); scrollToSection('try-demo'); }} style={{
              padding: isMobile ? '8px 12px' : '12px 24px', fontSize: isMobile ? '12px' : '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
              background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`, color: '#78350f',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)',
            }}>{isMobile ? 'Demo' : '▶ View Demo'}</button>
            <button onClick={() => navigate('/login')} style={{
              padding: isMobile ? '8px 12px' : '12px 24px', fontSize: isMobile ? '12px' : '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
              background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
              boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)',
            }}>{isMobile ? 'Login' : 'Organizer Login'}</button>
          </div>
        </div>
        {/* Phone link row: Products opens with a tap; no hover menus on a phone. */}
        {isMobile && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '4px', marginTop: '6px' }}>
            <ProductsMenu isMobile location="home_header_mobile" />
            <SpaLink to="/games" onClick={() => trackEvent('games_link_click', { location: 'header_mobile' })} style={{ ...navLink, padding: '10px 12px' }}>Games</SpaLink>
            <SpaLink to="/guides" style={{ ...navLink, padding: '10px 12px' }}>Guides</SpaLink>
          </div>
        )}
      </nav>

      {/* Hero — speaks for all of Tee Time Organizer */}
      <section style={{ padding: isMobile ? '140px 20px 28px' : '128px 32px 40px', background: 'white' }}>
        <div style={{ maxWidth: '880px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px', background: colors.green100, color: colors.green800,
            padding: '8px 16px', borderRadius: '50px', fontSize: '13px', fontWeight: 600, marginBottom: '22px',
            border: '1px solid rgba(22, 101, 52, 0.1)',
          }}>⭐ Simple. Fast. Reliable.</div>
          <h1 style={{
            fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '34px' : '54px', fontWeight: 400, color: colors.gray900,
            lineHeight: 1.08, margin: '0 0 20px', letterSpacing: isMobile ? '-0.5px' : '-1px',
          }}>
            Golf, organized — from the weekly game to the <span style={{ color: colors.green700 }}>big tournament</span>
          </h1>
          <p style={{ fontSize: isMobile ? '17px' : '20px', color: colors.gray600, lineHeight: 1.55, margin: '0 auto', maxWidth: '720px' }}>
            One simple tool for the people who run golf: tee times and sign-ups, pairings, games, live scoring, player stats and GHIN posting. The golf group organizer that does everything — and runs your tournament too.
          </p>
        </div>
      </section>

      {/* Products — "Choose what you run" */}
      <section id="products" aria-labelledby="products-heading" style={{ padding: isMobile ? '8px 16px 40px' : '8px 32px 64px', background: 'white' }}>
        <div style={{ maxWidth: compactCards ? '1200px' : '980px', margin: '0 auto' }}>
          <h2 id="products-heading" style={{
            textAlign: 'center', fontSize: isMobile ? '13px' : '15px', fontWeight: 700, letterSpacing: '1.5px',
            textTransform: 'uppercase', color: colors.gray500, margin: '0 0 20px',
          }}>Choose what you run</h2>
          <div style={{ display: 'grid', gridTemplateColumns: cardColumns, gap: isMobile ? '14px' : '20px' }}>
            {LIVE_PRODUCTS.map((p) => (
              <article key={p.key} style={{
                display: 'flex', flexDirection: 'column', gap: '14px',
                padding: isMobile ? '20px' : (compactCards ? '22px' : '32px'),
                border: `1px solid ${colors.gray200}`, borderRadius: '18px', background: 'white',
                boxShadow: '0 4px 18px rgba(17, 24, 39, 0.06)',
              }}>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '14px', background: colors.green100, color: colors.green800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <ProductIcon productKey={p.key} size={28} />
                </div>
                <h3 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400, fontSize: isMobile ? '26px' : (compactCards ? '24px' : '32px'), lineHeight: 1.15, margin: 0, color: colors.gray900 }}>
                  {p.name}
                </h3>
                <p style={{ fontSize: isMobile ? '16px' : '17px', lineHeight: 1.5, color: colors.gray700, margin: 0 }}>“{p.voice}”</p>
                {!compactCards && p.bullets.length > 0 && (
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {p.bullets.map((b) => (
                      <li key={b} style={{ display: 'flex', gap: '10px', fontSize: isMobile ? '15px' : '16px', color: colors.gray700 }}>
                        <span aria-hidden="true" style={{ color: colors.green600, fontWeight: 800 }}>✓</span>{b}
                      </li>
                    ))}
                  </ul>
                )}
                <div style={{ marginTop: 'auto', paddingTop: '6px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ fontSize: '15px', fontWeight: 700, color: colors.green800 }}>{p.price}</div>
                  <SpaLink to={p.to} onClick={() => trackEvent('product_card_click', { product: p.key })} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50px', padding: '0 20px',
                    borderRadius: '12px', background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`,
                    color: 'white', fontSize: '16px', fontWeight: 700, textDecoration: 'none',
                    boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)',
                  }}>{p.cta} &rarr;</SpaLink>
                </div>
              </article>
            ))}
          </div>
          {LIVE_PRODUCTS.length > 2 && (
            <p style={{ textAlign: 'center', margin: '24px 0 0', fontSize: '17px', color: colors.gray700 }}>
              <strong>One account.</strong> Start with what you run, add the rest when you need it.
            </p>
          )}
        </div>
      </section>

      {/* Everything in one place — what the products share */}
      <section id="features" style={{ padding: isMobile ? '40px 16px' : '64px 32px', background: colors.gray50, borderTop: `1px solid ${colors.gray200}` }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400, fontSize: isMobile ? '28px' : '40px', textAlign: 'center', color: colors.gray900, margin: '0 0 10px', letterSpacing: '-0.5px' }}>Everything in one place</h2>
          <p style={{ textAlign: 'center', fontSize: isMobile ? '16px' : '18px', color: colors.gray600, margin: '0 auto 32px', maxWidth: '680px', lineHeight: 1.5 }}>
            The same tools power every product. Players never pay and never download an app.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(240px, 1fr))', gap: isMobile ? '10px' : '16px' }}>
            {sharedFeatures.map((f) => (
              <div key={f.title} style={{ background: 'white', border: `1px solid ${colors.gray200}`, borderRadius: '14px', padding: isMobile ? '16px' : '22px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <h3 style={{ fontSize: '17px', fontWeight: 700, color: colors.gray900, margin: 0 }}>{f.title}</h3>
                <p style={{ fontSize: '15px', lineHeight: 1.5, color: colors.gray600, margin: 0 }}>{f.desc}</p>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: 'auto', paddingTop: '4px' }}>
                  {f.groups && <span style={{ fontSize: '12px', fontWeight: 700, color: colors.green800, background: colors.green100, padding: '4px 10px', borderRadius: '50px' }}>Weekly Golf Groups</span>}
                  {f.events && <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e3a8a', background: '#dbeafe', padding: '4px 10px', borderRadius: '50px' }}>Tournaments &amp; Outings</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Games band (Oct 5, 2026). Names come from gamesData.js, the same list as /games. */}
      <section id="games" style={{ padding: isMobile ? '36px 20px' : '48px 32px', background: 'white', borderBottom: `1px solid ${colors.gray200}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{
            display: 'inline-block', background: colors.green100, color: colors.green800,
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '14px',
          }}>{GAME_COUNT} games &middot; scored live &middot; settled for you</div>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '28px' : '36px', color: colors.gray900, margin: '0 0 10px', letterSpacing: '-0.5px' }}>
            Play the games your group already plays
          </h2>
          <p style={{ fontSize: isMobile ? '15px' : '17px', color: colors.gray500, lineHeight: 1.6, maxWidth: '680px', margin: '0 auto 22px' }}>
            Pick the games, set the wager, and score on your phones. Tee Time Organizer does the handicap math, keeps the games live as each score goes in, and works out who pays whom.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px', maxWidth: '860px', margin: '0 auto 24px' }}>
            {[...GROUP_GAMES, ...COMPETITIONS].map((g) => (
              <span key={g.name} style={{
                background: 'white', border: `1px solid ${colors.gray200}`, borderRadius: '50px', padding: '7px 14px',
                fontSize: isMobile ? '13px' : '14px', fontWeight: 600, color: colors.gray700,
              }}>
                {g.name}{g.isNew && <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 800, color: '#92400e', background: '#fef3c7', borderRadius: '4px', padding: '1px 5px' }}>NEW</span>}
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px' }}>
            <SpaLink to="/games" onClick={() => trackEvent('games_link_click', { location: 'home_band' })} style={{
              display: 'inline-block', padding: '12px 24px', fontSize: '15px', fontWeight: 700, borderRadius: '10px', textDecoration: 'none',
              background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
              boxShadow: '0 2px 8px rgba(22, 101, 52, 0.3)',
            }}>See all the games &rarr;</SpaLink>
            <a href="/downloads/TTO_Games.pdf" target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('games_pdf_click', { location: 'home_band' })} style={{
              display: 'inline-block', padding: '12px 22px', fontSize: '15px', fontWeight: 700, borderRadius: '10px', textDecoration: 'none',
              background: 'white', color: colors.green800, border: `2px solid ${colors.green700}`,
            }}>Download the fact sheet (PDF)</a>
          </div>
          <p style={{ fontSize: '14px', color: colors.gray500, margin: '16px 0 0' }}>
            {GAMES_PLAN}.{' '}
            <SpaLink to="/groups#pricing" style={{ color: colors.green700, fontWeight: 600 }}>See plans &amp; pricing</SpaLink>
          </p>
        </div>
      </section>

      {/* Demo Section */}
      <section id="try-demo" style={{ padding: isMobile ? '40px 20px' : '56px 32px', background: `linear-gradient(135deg, ${colors.green800} 0%, ${colors.green900} 100%)`, textAlign: 'center' }}>
        <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400, fontSize: isMobile ? '28px' : '38px', color: 'white', margin: '0 0 12px' }}>See it before you sign up</h2>
        <p style={{ fontSize: isMobile ? '15px' : '17px', color: colors.green100, margin: '0 auto 26px', maxWidth: '620px', lineHeight: 1.5 }}>
          Try the organizer dashboard and the player app with sample data, or score a sample tournament. No signup required.
        </p>
        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => { trackEvent('demo_viewed', { type: 'organizer_dashboard' }); window.location.href = '/dashboard?demo=true&clear=auth'; }} style={{
            padding: isMobile ? '12px 20px' : '14px 28px', minHeight: '48px', fontSize: isMobile ? '14px' : '15px', fontWeight: 700, borderRadius: '10px', border: 'none', cursor: 'pointer',
            background: `linear-gradient(135deg, ${colors.amber400} 0%, ${colors.amber500} 100%)`, color: '#78350f',
          }}>🎯 View Organizer Dashboard</button>
          <button onClick={() => { trackEvent('demo_viewed', { type: 'player_portal' }); window.open('/p/s3DhoI', '_blank'); }} style={{
            padding: isMobile ? '12px 20px' : '14px 28px', minHeight: '48px', fontSize: isMobile ? '14px' : '15px', fontWeight: 700, borderRadius: '10px', border: 'none', cursor: 'pointer',
            background: 'white', color: colors.green900,
          }}>📱 View Player Portal</button>
          <SpaLink to="/try/scoring" onClick={() => trackEvent('demo_viewed', { type: 'sample_tournament' })} style={{
            display: 'inline-flex', alignItems: 'center', padding: isMobile ? '12px 20px' : '14px 28px', minHeight: '48px', boxSizing: 'border-box',
            fontSize: isMobile ? '14px' : '15px', fontWeight: 700, borderRadius: '10px', textDecoration: 'none',
            border: '2px solid rgba(255, 255, 255, 0.6)', color: 'white',
          }}>⛳ Score a Sample Tournament</SpaLink>
        </div>
      </section>

      {/* Testimonials */}
      <section style={{ padding: isMobile ? '36px 20px' : '56px 32px', background: 'white' }}>
        <header style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto 32px' }}>
          <div style={{
            display: 'inline-block', background: '#fef3c7', color: '#92400e',
            padding: '6px 14px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px',
          }}>★ Testimonials</div>
          <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontSize: isMobile ? '30px' : '38px', color: colors.gray900, margin: 0, letterSpacing: '-0.5px' }}>Trusted by Golf Groups & Leagues</h2>
        </header>
        <div style={{
          maxWidth: '820px', margin: '0 auto',
          display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: isMobile ? '14px' : '20px',
        }}>
          {[
            {
              initials: 'DK', name: 'Dennis Kirwin', location: 'Mount Pleasant, SC', role: 'Organizer',
              text: "The app is a life saver — the days of managing text threads are over. Players sign up weeks in advance, and I love being able to form teams by handicap so everyone can see the pairings before showing up at the course.",
            },
            {
              initials: 'ST', name: 'Sam T.', location: 'Loveland, OH', role: 'Player',
              text: "I can RSVP from my phone in 10 seconds. No more 'reply all' text chains. The calendar sync is clutch.",
              accent: 'amber',
            },
            {
              initials: 'TM', name: 'Todd M.', location: 'Charleston, SC', role: 'Organizer',
              text: "With 75+ golfers, collecting RSVPs, updating handicaps, and building teams used to take hours every week. Now it's about 15 minutes — and the members love managing their own RSVPs.",
            },
            {
              initials: 'DM', name: 'Dan Moylan', location: 'The Rick Kelly Memorial Golf League', role: 'Organizer',
              text: "I manage 100+ league members. Before TTO, players signed up by replying to my daily email and I built the tee sheet by hand. Now signup is automated and the 'Reply All' nightmare is gone. After 15+ years organizing, it's worth a test drive.",
              accent: 'amber',
            },
          ].map((t, i) => (
            <article key={i} style={{
              background: 'white', border: `1px solid ${colors.gray200}`, borderRadius: '14px',
              padding: isMobile ? '20px' : '22px', display: 'flex', flexDirection: 'column',
            }}>
              <div style={{ color: colors.amber400, fontSize: '16px', marginBottom: '10px', letterSpacing: '2px' }}>★★★★★</div>
              <p style={{ fontSize: '15px', color: colors.gray700, lineHeight: 1.6, margin: '0 0 16px 0', fontStyle: 'italic', flexGrow: 1 }}>"{t.text}"</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: t.accent === 'amber'
                    ? `linear-gradient(135deg, ${colors.amber400}, ${colors.amber500})`
                    : `linear-gradient(135deg, ${colors.green700}, ${colors.green900})`,
                  color: t.accent === 'amber' ? '#78350f' : 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: '13px', flexShrink: 0,
                }}>{t.initials}</div>
                <div>
                  <div style={{ fontWeight: 700, color: colors.gray900, fontSize: '14px' }}>{t.name}</div>
                  <div style={{ fontSize: '12px', color: colors.gray500 }}>
                    {t.location}
                    <span style={{
                      marginLeft: '8px', padding: '1px 7px', borderRadius: '10px',
                      background: colors.gray100, color: colors.gray600,
                      fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}>{t.role}</span>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* FAQ — about Tee Time Organizer as a whole; each product page has its own */}
      <section id="faq" style={{ padding: isMobile ? '32px 20px' : '48px 32px', background: colors.gray50 }}>
        <h2 style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400, fontSize: isMobile ? '28px' : '36px', color: colors.gray900, textAlign: 'center', margin: '0 0 24px', letterSpacing: '-0.5px' }}>Questions</h2>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          {[
            { q: 'Which product do I need?', a: 'If you organize the same players every week — a buddy group, a big weekly game or a league — start with Weekly Golf Groups. If you’re running a one-day event such as a member-guest, a charity outing or a club championship, an Event Pass for Tournaments & Outings covers it. A weekly group can add an Event Pass to its own account when it hosts an event.' },
            { q: 'Do my players need to download an app?', a: 'No. Players use a web link — no app download, no account, no password. It works on any phone or computer with a browser, and players can add it to their home screen if they like.' },
            { q: 'How much does it cost?', a: 'Weekly golf groups are free for up to 8 regular players, with plans from $4.99 a month for groups up to 250. A tournament or outing is a one-time Event Pass starting at $135, with no subscription. Players never pay.' },
            { q: 'Does it post scores to GHIN?', a: 'Yes. On Starter and above, players can post their rounds to GHIN straight from Tee Time Organizer, and on Plus and above every player’s handicap index stays in sync with GHIN automatically.' },
          ].map((faq, i) => (
            <details key={i} style={{
              background: 'white', border: `1px solid ${colors.gray200}`, borderRadius: '10px',
              padding: '0', marginBottom: '8px', cursor: 'pointer',
            }}>
              <summary style={{
                padding: isMobile ? '14px 16px' : '15px 20px', fontSize: isMobile ? '15px' : '16px', fontWeight: 600, color: colors.gray900,
                listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span>{faq.q}</span>
                <span style={{ color: colors.gray400, fontSize: '16px', marginLeft: '12px', flexShrink: 0 }}>+</span>
              </summary>
              <div style={{ padding: isMobile ? '0 16px 14px' : '0 20px 16px', fontSize: '15px', color: colors.gray600, lineHeight: 1.6 }}>
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: isMobile ? '36px 20px 24px' : '52px 32px 28px', background: colors.gray900 }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr',
            gap: isMobile ? '28px' : '40px',
            marginBottom: '32px', paddingBottom: '28px',
            borderBottom: '1px solid rgba(255,255,255,0.1)',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
                <div style={{
                  width: '40px', height: '40px', background: 'white', borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                }}>
                  <img src="/logo.png" alt="Tee Time Organizer Logo" loading="lazy" width="32" height="32" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                </div>
                <div style={{ fontSize: '17px', fontWeight: 700, color: 'white' }}>Tee Time Organizer</div>
              </div>
              <p style={{ fontSize: '13px', color: colors.gray400, lineHeight: 1.7, margin: 0, maxWidth: '380px' }}>
                Golf group and tournament organizer: tee times and sign-ups, pairings, games, live scoring, player stats and GHIN posting, for weekly golf groups and for tournaments and outings.
              </p>
            </div>

            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'white', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px' }}>Products</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {LIVE_PRODUCTS.map((p) => (
                  <li key={p.key} style={{ marginBottom: '10px' }}>
                    <SpaLink to={p.to} onClick={() => trackEvent('product_footer_click', { product: p.key })} style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>{p.name}</SpaLink>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'white', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px' }}>Learn</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/games" onClick={() => trackEvent('games_link_click', { location: 'footer' })} style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Games</SpaLink>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/guides" style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Guides</SpaLink>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/compare" style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Compare</SpaLink>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <a href="#try-demo" onClick={(e) => { e.preventDefault(); scrollToSection('try-demo'); }} style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Live Demo</a>
                </li>
              </ul>
            </div>

            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: 'white', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '14px' }}>Company</div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{ marginBottom: '10px' }}>
                  <button type="button" onClick={() => setShowContactModal(true)} style={{ background: 'none', border: 'none', padding: 0, color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit' }}>Contact Us</button>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/login" style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Organizer Login</SpaLink>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <a href="https://www.facebook.com/profile.php?id=61586992568016" target="_blank" rel="noopener noreferrer" style={{ color: colors.gray300, fontSize: '14px', textDecoration: 'none' }}>Facebook ↗</a>
                </li>
                <li style={{ marginBottom: '10px' }}>
                  <SpaLink to="/privacy" style={{ color: colors.gray300, fontSize: '14px', cursor: 'pointer', textDecoration: 'none' }}>Privacy Policy</SpaLink>
                </li>
              </ul>
            </div>
          </div>

          <div style={{
            display: 'flex', flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '0',
          }}>
            <div style={{ fontSize: '12px', color: colors.gray400 }}>© 2025–2026 Tee Time Organizer. All rights reserved.</div>
            <div style={{ fontSize: '12px', color: colors.gray500 }}>Golf, organized — from the weekly game to the big tournament.</div>
          </div>
        </div>
      </footer>

      {/* Contact Modal */}
      {showContactModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={closeContactModal}>
          <div style={{ background: 'white', borderRadius: '16px', padding: isMobile ? '24px' : '32px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
            {contactSuccess ? (
              <div style={{ background: colors.green50, color: colors.green800, padding: '16px', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>✅</div>
                <div style={{ fontWeight: 700, marginBottom: '8px' }}>Message Sent!</div>
                <div style={{ fontSize: '14px' }}>We'll get back to you soon.</div>
                <button onClick={closeContactModal} style={{
                  marginTop: '20px', padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
                  background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
                }}>Close</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px', color: colors.gray900 }}>
                  {inquiryType === 'club' ? 'Talk to us about Club' : 'Contact Us'}
                </h3>
                <p style={{ fontSize: '14px', color: colors.gray500, marginBottom: '24px' }}>
                  {inquiryType === 'club'
                    ? 'Tell us a bit about your organization and we\'ll be in touch about pricing and onboarding.'
                    : 'Have questions? We\'d love to hear from you.'}
                </p>
                
                {contactError && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>{contactError}</div>}
                
                <form onSubmit={handleContactSubmit}>
                  {/* Bot trap. Off-screen rather than display:none (some scripts
                      skip hidden inputs), out of the tab order and hidden from
                      screen readers, so no keyboard or assistive-tech user can
                      reach it. Anything here came from a machine. */}
                  <input
                    type="text"
                    name="website"
                    value={contactForm.website}
                    onChange={e => setContactForm({ ...contactForm, website: e.target.value })}
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
                  />
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Name</label>
                    <input type="text" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} />
                  </div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Email</label>
                    <input type="email" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} />
                  </div>
                  {inquiryType === 'club' && (
                    <>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Organization / club name</label>
                        <input type="text" required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} value={contactForm.organizationName} onChange={e => setContactForm({ ...contactForm, organizationName: e.target.value })} />
                      </div>
                      <div style={{ marginBottom: '16px' }}>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Approximate group size</label>
                        <select required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', boxSizing: 'border-box', background: 'white' }} value={contactForm.approximateSize} onChange={e => setContactForm({ ...contactForm, approximateSize: e.target.value })}>
                          <option value="">Select a range…</option>
                          <option value="under_50">Under 50</option>
                          <option value="50_100">50–100</option>
                          <option value="100_250">100–250</option>
                          <option value="250_plus">250+</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, marginBottom: '6px', color: colors.gray700 }}>Message</label>
                    <textarea required style={{ width: '100%', padding: '12px 16px', fontSize: '14px', border: `1px solid ${colors.gray300}`, borderRadius: '8px', outline: 'none', minHeight: '120px', resize: 'vertical', boxSizing: 'border-box' }} value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })} />
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                    <button type="button" onClick={closeContactModal} style={{
                      flex: 1, padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', cursor: 'pointer',
                      background: 'white', color: colors.green800, border: `2px solid ${colors.green700}`,
                    }}>Cancel</button>
                    <button type="submit" disabled={contactSubmitting} style={{
                      flex: 1, padding: '12px 24px', fontSize: '14px', fontWeight: 600, borderRadius: '10px', border: 'none', cursor: 'pointer',
                      background: `linear-gradient(135deg, ${colors.green700} 0%, ${colors.green900} 100%)`, color: 'white',
                    }}>{contactSubmitting ? 'Sending...' : 'Send Message'}</button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
